import React, { useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button, Modal } from '@perch-labs/shrike';
import { ScrollablePdf } from 'pages/settings/secondary/contracts/personalized-contract/scrollable-pdf.component';
import { PdfDialog, ReviewDialogHeader } from './document-review-dialog.style';

export interface DocumentReviewDialogProps {
  base64Image: string;
  fileType: string;
  onApprove: (base64Image: string) => void;
  onCancel: () => void;
  onClose: () => void;
}

export const DocumentReviewDialog = ({
  base64Image,
  fileType,
  onApprove,
  onCancel,
  onClose,
}: DocumentReviewDialogProps) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [htmlImage, setHtmlImage] = useState<HTMLImageElement | null>(null);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const eventHtmlImage = e.currentTarget;
    setHtmlImage(eventHtmlImage);
    setCompletedCrop({
      unit: 'px',
      x: 0,
      y: 0,
      width: eventHtmlImage.width,
      height: eventHtmlImage.height,
    });
  }

  const isPdf = fileType === 'application/pdf';
  const onOk = () => {
    if (isPdf) {
      // For PDFs, just approve the original base64 without cropping
      onApprove(base64Image);
      return;
    }

    // For images, perform cropping
    const scaleX = htmlImage!.naturalWidth / htmlImage!.width;
    const scaleY = htmlImage!.naturalHeight / htmlImage!.height;

    const fullScaleCropWidth = completedCrop!.width * scaleX;
    const fullScaleCropHeight = completedCrop!.height * scaleY;
    const fullScaleCropX = completedCrop!.x * scaleX;
    const fullScaleCropY = completedCrop!.y * scaleY;

    const canvas = document.createElement('canvas');
    canvas.width = fullScaleCropWidth;
    canvas.height = fullScaleCropHeight;
    const canvasContext = canvas.getContext('2d');
    canvasContext!.drawImage(
      htmlImage!,
      fullScaleCropX,
      fullScaleCropY,
      fullScaleCropWidth,
      fullScaleCropHeight,
      0,
      0,
      fullScaleCropWidth,
      fullScaleCropHeight
    );
    const croppedBase64Image = canvas.toDataURL(fileType);
    onApprove(croppedBase64Image);
  };

  const content = (
    <>
      {isPdf ? (
        <PdfDialog>
          <ScrollablePdf pdfUrl={base64Image} />
        </PdfDialog>
      ) : (
        // For images, show the cropping interface
        <ReactCrop crop={crop} onChange={setCrop} onComplete={setCompletedCrop}>
          <img src={base64Image} alt="Uploaded document" onLoad={onImageLoad} />
        </ReactCrop>
      )}
      <Button
        fullWidth
        disabled={!isPdf && !completedCrop}
        onClick={onOk}
        margin={{ top: '32px', bottom: '16px' }}
      >
        {isPdf ? 'Save Document' : 'Save Image'}
      </Button>
      <Button backgroundless fullWidth onClick={onCancel}>
        Cancel
      </Button>
    </>
  );

  return (
    <Modal size="small" aria-label="review-and-crop">
      <ReviewDialogHeader
        title={isPdf ? 'Review document' : 'Review and crop image'}
        onClose={onClose}
      />
      <Modal.Content>{content}</Modal.Content>
    </Modal>
  );
};
